<div data-testid="inbound-form-container" class="flex flex-col overflow-x-hidden">
  <!-- Header with action buttons -->
  <div
    class="flex justify-between items-center sticky top-0 bg-white z-10 px-6 pt-4"
  >
    <h2 class="text-2xl font-semibold text-gray-800">
      {{ getPageTitle() }}
    </h2>
    <div class="action-buttons mt-2">
      @for (
        action of getHeaderActions();
        track action.action + (action.data?.statusCode || "")
      ) {
        <app-button
          [testId]="'inbound-form-header-action-' + action.action"
          [label]="action.label | translate"
          [icon]="action.icon"
          (emitOnClick)="
            onFormAction({ action: action.action, data: action.data })
          "
          [outlined]="action.outlined || false"
          [type]="action.severity || 'info'"
          [disabled]="action.disabled || false"
          [class]="'min-w-fit'"
          [loading]="action.loading || false"
        ></app-button>
      }
    </div>
  </div>

  <!-- Scrollable content area -->
  <div class="flex-1 min-h-0 overflow-auto px-6">
    <div class="p-2">
      <!-- Status Selection Section removed - now using inline action buttons -->

      <!-- Basic Information Section -->
      <section data-testid="inbound-form-section-basic-information" class="form-section mb-4">
        <div class="flex items-center mb-2">
          <h2 class="section-title text-lg font-semibold mr-4">
            {{ "inbound.sections.basic_information" | translate }}
          </h2>
        </div>
        <div class="grid gap-2 px-4 py-2 grid-cols-5">
          @for (
            field of getFieldsBySection("inbound.sections.basic_information");
            track field.controlName
          ) {
            @if (!field.hidden) {
              <div class="form-field">
                <label class="form-label text-sm">
                  {{ field.label | translate }}
                  @if (field.required) {
                    <span class="text-red-500">*</span>
                  }
                </label>
                <input
                  [attr.data-testid]="'inbound-form-input-' + field.controlName"
                  pInputText
                  [value]="getFieldValue(field) || ' '"
                  [placeholder]="field.placeholder | translate"
                  [disabled]="!addMode"
                  class="w-full text-sm"
                />
              </div>
            }
          }
        </div>
      </section>

      <!-- Customer Information Section (for Purchase Orders) - Full width 5-column grid -->
      @if (showCustomerInformation()) {
        <section data-testid="inbound-form-section-customer-information" class="form-section mb-4">
          <div class="flex items-center mb-2">
            <h2 class="section-title text-lg font-semibold mr-4">
              {{ "inbound.sections.customer_information" | translate }}
            </h2>
          </div>
          <div class="px-4 py-1">
            <div
              [class]="
                getGridLayoutClass('inbound.sections.customer_information')
              "
            >
              @for (
                field of getFieldsBySection(
                  "inbound.sections.customer_information"
                );
                track field.controlName
              ) {
                @if (!field.hidden) {
                  <div
                    class="form-field"
                    [class]="
                      getFieldWidthClass(
                        'inbound.sections.customer_information'
                      )
                    "
                  >
                    <label class="form-label text-sm">
                      {{ field.label | translate }}
                    </label>
                    <input
                      [attr.data-testid]="'inbound-form-input-' + field.controlName"
                      pInputText
                      [value]="getFieldValue(field) || ' '"
                      [placeholder]="field.placeholder | translate"
                      [disabled]="!addMode"
                      class="w-full text-sm"
                    />
                  </div>
                }
              }
            </div>
          </div>
        </section>
      }

      <!-- Returner Information Section (for Return Orders) - Full width 5-column grid -->
      @if (showReturnerInformation()) {
        <section data-testid="inbound-form-section-returner-information" class="form-section mb-4">
          <div class="flex items-center mb-2">
            <h2 class="section-title text-lg font-semibold mr-4">
              {{ "inbound.sections.returner_information" | translate }}
            </h2>
          </div>
          <div class="px-4 py-1">
            <div
              [class]="
                getGridLayoutClass('inbound.sections.returner_information')
              "
            >
              @for (
                field of getFieldsBySection(
                  "inbound.sections.returner_information"
                );
                track field.controlName
              ) {
                @if (!field.hidden) {
                  <div
                    class="form-field"
                    [class]="
                      getFieldWidthClass(
                        'inbound.sections.returner_information'
                      )
                    "
                  >
                    <label class="form-label text-sm">
                      {{ field.label | translate }}
                    </label>
                    <input
                      [attr.data-testid]="'inbound-form-input-' + field.controlName"
                      pInputText
                      [value]="getFieldValue(field) || ' '"
                      [placeholder]="field.placeholder | translate"
                      [disabled]="!addMode"
                      class="w-full text-sm"
                    />
                  </div>
                }
              }
            </div>
          </div>
        </section>
      }

      <!-- Grouped sections with 1-2 fields in 2-column outer grid -->
      <div [class]="getOuterGridClass()">
        <!-- Warehouse Information Section (for Transfer Orders only) -->
        @if (showWarehouseInformation()) {
          <section data-testid="inbound-form-section-warehouse-information" class="form-section mb-4">
            <div class="flex items-center mb-2">
              <h2 class="section-title text-lg font-semibold mr-4">
                {{ "inbound.sections.warehouse_information" | translate }}
              </h2>
            </div>
            <div class="px-4 py-1">
              <div
                [class]="
                  getGridLayoutClass('inbound.sections.warehouse_information')
                "
              >
                @for (
                  field of getFieldsBySection(
                    "inbound.sections.warehouse_information"
                  );
                  track field.controlName
                ) {
                  @if (!field.hidden) {
                    <div
                      class="form-field"
                      [class]="
                        getFieldWidthClass(
                          'inbound.sections.warehouse_information'
                        )
                      "
                    >
                      <label class="form-label text-sm">
                        {{ field.label | translate }}
                      </label>
                      <input
                        [attr.data-testid]="'inbound-form-input-' + field.controlName"
                        pInputText
                        [value]="getFieldValue(field) || ' '"
                        [placeholder]="field.placeholder | translate"
                        [disabled]="!addMode"
                        class="w-full text-sm"
                      />
                    </div>
                  }
                }
              </div>
            </div>
          </section>
        }

        <!-- Store Information Section (for Transfer Orders) -->
        @if (showStoreInformation()) {
          <section data-testid="inbound-form-section-store-information" class="form-section mb-4">
            <div class="flex items-center mb-2">
              <h2 class="section-title text-lg font-semibold mr-4">
                {{ "inbound.sections.store_information" | translate }}
              </h2>
            </div>
            <div class="px-4 py-1">
              <div
                [class]="
                  getGridLayoutClass('inbound.sections.store_information')
                "
              >
                @for (
                  field of getFieldsBySection(
                    "inbound.sections.store_information"
                  );
                  track field.controlName
                ) {
                  @if (!field.hidden) {
                    <div
                      class="form-field"
                      [class]="
                        getFieldWidthClass('inbound.sections.store_information')
                      "
                    >
                      <label class="form-label text-sm">
                        {{ field.label | translate }}
                      </label>
                      <input
                        [attr.data-testid]="'inbound-form-input-' + field.controlName"
                        pInputText
                        [value]="getFieldValue(field) || ' '"
                        [placeholder]="field.placeholder | translate"
                        [disabled]="!addMode"
                        class="w-full text-sm"
                      />
                    </div>
                  }
                }
              </div>
            </div>
          </section>
        }

        <!-- Logistics Information Section (2 fields) -->
        <section data-testid="inbound-form-section-logistics-information" class="form-section mb-4">
          <div class="flex items-center mb-2">
            <h2 class="section-title text-lg font-semibold mr-4">
              {{ "inbound.sections.logistics_information" | translate }}
            </h2>
          </div>
          <div class="px-4 py-1">
            <div
              [class]="
                getGridLayoutClass('inbound.sections.logistics_information')
              "
            >
              @for (
                field of getFieldsBySection(
                  "inbound.sections.logistics_information"
                );
                track field.controlName
              ) {
                @if (!field.hidden) {
                  <div
                    class="form-field"
                    [class]="
                      getFieldWidthClass(
                        'inbound.sections.logistics_information'
                      )
                    "
                  >
                    <label class="form-label text-sm">
                      {{ field.label | translate }}
                    </label>
                    <input
                      [attr.data-testid]="'inbound-form-input-' + field.controlName"
                      pInputText
                      [value]="getFieldValue(field) || ' '"
                      [placeholder]="field.placeholder | translate"
                      [disabled]="!addMode"
                      class="w-full text-sm"
                    />
                  </div>
                }
              }
            </div>
          </div>
        </section>
      </div>

      <!-- Item Details Section -->
      @for (section of tableSections; track section.title; let sectionIndex = $index) {
        <section
          class="form-section mb-4"
          [attr.data-testid]="'inbound-form-section-table-' + sectionIndex"
        >
          <div class="flex items-center mb-2">
            <h2 class="section-title text-lg font-semibold mr-4">
              {{ section.title | translate }}
            </h2>
          </div>
          <div class="px-4 py-1">
            <app-data-table
              [attr.data-testid]="'inbound-form-table-' + sectionIndex"
              [columns]="section.columns"
              [dataSource]="section.data"
              [loading]="section.loading || false"
              [hasCheckColumn]="section.hasCheckbox || false"
              [totalRecords]="section.totalRecords || 0"
              [first]="section.first || 0"
              [rows]="section.rows || 20"
            ></app-data-table>
          </div>
        </section>
      }
    </div>
  </div>
</div>
